import * as React from 'react';
import ScrollToTop from "react-scroll-to-top";
import ChapterNav from './components/ChapterNav';
import MetricCards from './components/MetricCards';
import PullQuote from './components/PullQuote';
import CompactImageCard from './components/CompactImageCard';
import ImageSubsection from './components/ImageSubsection';
import Footer from "../../toolbar/Footer";
import image1 from '../../images/aistudio1.jpeg';
import image2 from '../../images/aistudio11.jpg';
import imageDefaultPng from '../../images/aistudio/Default.png';
import imageEditCandidateSet from '../../images/aistudio/EditCandidateSet.png';
import imageModelValidationPreview from '../../images/aistudio/ModelValidationPreview.png';
import imagePublishModelForm from '../../images/aistudio/PublishModelForm.png';
import imageRuleDialog from '../../images/aistudio/RuleDialog.jpg';
import imageSelectMachineLearningTemplate from '../../images/aistudio/SelectMachineLearningTemplate.png';
import imageAiStudioBanner3 from '../../images/aistudio/aibanner3.png';
import imagePersona1 from '../../images/aistudio/persona1.png';
import imagePersona2 from '../../images/aistudio/persona2.png';
import imagePersona3 from '../../images/aistudio/persona3.png';
import imageSketch1 from '../../images/aistudio/ai_sketch1.jpg';
import imageSketch2 from '../../images/aistudio/ai_sketch2.jpg';
import imageSketch3 from '../../images/aistudio/ai_sketch3.jpg';
import './AIStudio.css';
import PageTransition from '../../pageTransition';

const CHAPTERS = [
    { id: 'ch-ai-context', label: 'Challenge' },
    { id: 'ch-ai-users', label: 'Users' },
    { id: 'ch-ai-process', label: 'Strategy' },
    { id: 'ch-ai-design', label: 'Design' },
    { id: 'ch-ai-impact', label: 'Impact' },
];

const METRICS = [
    { value: 100, suffix: '%', label: 'No-Code Model Building' },
];

const AIStudio = (props) => {
    return (
        <>
        <PageTransition>
        <div className="mainBox">
            <div className="aiStudio project">
                <div className="project-inner">
                    <ImageSubsection
                        title="AI Studio"
                        banner={true}
                        images={[
                            { image: imageAiStudioBanner3, width: '60rem', height: '35rem' },
                        ]}
                        description='Led design strategy for an enterprise AI platform, from concept workshops to shipped product<br/><br/><span style="opacity: 0.6">ROLE:</span> Lead Designer'
                    />

                    <div className="aistudio-spreads">
                        {/* ========================================= */}
                        {/* SPREAD 1: Context                         */}
                        {/* ========================================= */}
                        <div className="spread-ai-context" id="ch-ai-context">
                            <div className="spread-cell--chapter" style={{ gridArea: 'ch' }}>
                                <span className="spread-chapter-number">01</span>
                                <span className="spread-chapter-label">Challenge</span>
                                <p className="spread-chapter-desc">
                                    Squirro uses Augmented Intelligence to extract insights from user data. AI Studio is the workspace for building and training text classification models using a no-code/low-code interface. The challenge: design a standardized framework that integrates various ML technologies while remaining accessible to non-technical users.
                                </p>
                            </div>

                            <div className="spread-cell--hero-img" style={{ gridArea: 'oi' }}>
                                <CompactImageCard
                                    image={imageEditCandidateSet}
                                    title="AI Studio — Candidate Set Editor"
                                />
                            </div>
                        </div>

                        {/* ========================================= */}
                        {/* SPREAD 2: Users                           */}
                        {/* ========================================= */}
                        <div className="spread-ai-users" id="ch-ai-users">
                            <div className="spread-cell--chapter" style={{ gridArea: 'ch' }}>
                                <span className="spread-chapter-number">02</span>
                                <span className="spread-chapter-label">Users</span>
                                <p className="spread-chapter-desc">
                                    Three distinct user groups interact with the AI Studio at different stages of the model lifecycle — from selecting pre-trained models to uploading custom ones.
                                </p>
                            </div>

                            <div className="spread-cell--card1" style={{ gridArea: 'c1' }}>
                                <CompactImageCard
                                    image={imagePersona3}
                                />
                            </div>
                            <div className="spread-cell--card2" style={{ gridArea: 'c2' }}>
                                <CompactImageCard
                                    image={imagePersona1}
                                />
                            </div>
                            <div className="spread-cell--card3" style={{ gridArea: 'c3' }}>
                                <CompactImageCard
                                    image={imagePersona2}
                                />
                            </div>
                        </div>

                        {/* ========================================= */}
                        {/* SPREAD 3: Process                         */}
                        {/* ========================================= */}
                        <div className="spread-ai-process" id="ch-ai-process">
                            <div className="spread-cell--strategy-text" style={{ gridArea: 'st' }}>
                                <span className="spread-chapter-number">03</span>
                                <span className="spread-chapter-label">Strategy</span>
                                <h3>The Toyota Way Assembly Line</h3>
                                <p>
                                    Every engineer had different ideas about how to build a model end-to-end. After a process flow workshop with our CTO, we adopted the assembly line approach from The Toyota Way — where complex systems flow through repeatable, value-adding processes without interruption. This led to five components: Candidate Sets, Ground Truth, Model, Validation, and Publish.
                                </p>
                            </div>

                            <div className="spread-cell--flowchart" style={{ gridArea: 'fi' }}>
                                <CompactImageCard
                                    image={image1}
                                    title="Process Flow Workshop Output"
                                />
                            </div>

                            <div className="spread-cell--workshop" style={{ gridArea: 'ws' }}>
                                <div className="spread-cell--workshop-text">
                                    <h3>Concept Sketches</h3>
                                    <p>Early ideation exploring layout patterns for the assembly-line interface — balancing complex ML workflows with an approachable no-code experience.</p>
                                </div>
                                <div className="spread-cell--workshop-thumbs">
                                    <CompactImageCard image={imageSketch1} />
                                    <CompactImageCard image={imageSketch2} />
                                    <CompactImageCard image={imageSketch3} />
                                </div>
                            </div>
                        </div>

                        {/* ========================================= */}
                        {/* SPREAD 4: Design                          */}
                        {/* ========================================= */}
                        <div className="spread-ai-design" id="ch-ai-design">
                            <div className="spread-cell--chapter" style={{ gridArea: 'ch' }}>
                                <span className="spread-chapter-number">04</span>
                                <span className="spread-chapter-label">Design</span>
                                <p className="spread-chapter-desc">
                                    The final designs were built on Figma using Google Material Design components. A custom navigation lets users select tabs to begin at any step, with breadcrumbs replacing tabs once inside. The FAB provides an easy exit back to the home page.
                                </p>
                            </div>

                            <div className="spread-cell--card1" style={{ gridArea: 'c1' }}>
                                <CompactImageCard
                                    image={image2}
                                    title="Assembly Line Overview"
                                />
                            </div>
                            <div className="spread-cell--card2" style={{ gridArea: 'c2' }}>
                                <CompactImageCard
                                    image={imageDefaultPng}
                                    title="Ground Truth — Default View"
                                />
                            </div>
                            <div className="spread-cell--card3" style={{ gridArea: 'c3' }}>
                                <CompactImageCard
                                    image={imageRuleDialog}
                                    title="Rule Configuration Dialog"
                                />
                            </div>
                            <div className="spread-cell--card4" style={{ gridArea: 'c4' }}>
                                <CompactImageCard
                                    image={imageSelectMachineLearningTemplate}
                                    title="ML Template Selection"
                                />
                            </div>
                            <div className="spread-cell--card5" style={{ gridArea: 'c5' }}>
                                <CompactImageCard
                                    image={imageModelValidationPreview}
                                    title="Model Validation Preview"
                                />
                            </div>
                            <div className="spread-cell--card6" style={{ gridArea: 'c6' }}>
                                <CompactImageCard
                                    image={imagePublishModelForm}
                                    title="Publish Model"
                                />
                            </div>

                            <div className="spread-cell--video" style={{ gridArea: 'vd' }}>
                                <iframe
                                    src="https://www.youtube.com/embed/9cyYtD3eZmA"
                                    allow="autoplay; encrypted-media"
                                    allowFullScreen
                                    title="AI Studio Demo"
                                />
                            </div>

                        </div>

                        {/* ===== IMPACT METRICS ===== */}
                        <section id="ch-ai-impact">
                            <MetricCards title="Impact" metrics={METRICS} />
                            <PullQuote
                                text="Not finding a comparable product was a blessing in disguise — we stayed uninfluenced and set out to build the best product in this category."
                                variant="warm"
                            />
                        </section>
                    </div>
                </div>
            </div>
            <Footer/>
        </div>
        </PageTransition>
        <ScrollToTop smooth className="scroll-to-top" />
        <ChapterNav chapters={CHAPTERS} />
        </>
    );
};

export default AIStudio;
